import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { useState, FormEvent } from 'react';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';
import { useSEO, getWebPageSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.21daybusinesswebsite.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      getWebPageSchema(
        'Contact - Démarrez votre projet',
        'Contactez-nous pour discuter de votre projet de site web business. Réponse garantie sous 24h.',
        `${siteUrl}/contact`
      ),
      getBreadcrumbSchema([
        { name: 'Accueil', url: '/' },
        { name: 'Contact', url: '/contact' }
      ]),
      {
        '@type': 'ContactPage',
        name: 'Contact',
        description: 'Formulaire de contact pour démarrer votre projet'
      }
    ]
  };

  useSEO({
    title: 'Contact - Démarrez votre projet | THE 21-DAY BUSINESS WEBSITE™',
    description: 'Contactez-nous pour discuter de votre projet de site web business. Réponse garantie sous 24h. Email, téléphone ou formulaire de contact.',
    keywords: 'contact agence web, devis site web, demande projet web, contacter agence',
    canonical: `${siteUrl}/contact`,
    schema
  });

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Envoi uniquement vers le webhook n8n
      const webhookData = {
        prenom: formData.prenom,
        nom: formData.nom,
        email: formData.email,
        telephone: formData.telephone,
        message: formData.message,
        source: 'SITE 21DAYSSITE',
        timestamp: new Date().toISOString()
      };

      const response = await fetch('https://n8n.webstorm.fr/webhook/wordpress-lead-whatsapp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(webhookData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        navigate('/merci');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Erreur webhook:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Démarrez votre <span className="text-yellow-500">projet</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Contactez-nous pour discuter de votre projet et recevoir une proposition personnalisée
            </p>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Form */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-8">
                  Parlez-nous de votre projet
                </h2>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="prenom" className="block text-sm font-medium text-slate-700 mb-2">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        id="prenom"
                        name="prenom"
                        required
                        value={formData.prenom}
                        onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="Votre prénom"
                      />
                    </div>

                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-slate-700 mb-2">
                        Nom *
                      </label>
                      <input
                        type="text"
                        id="nom"
                        name="nom"
                        required
                        value={formData.nom}
                        onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent outline-none transition-all text-sm"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="votre@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="telephone" className="block text-sm font-medium text-slate-700 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      name="telephone"
                      value={formData.telephone}
                      onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent outline-none transition-all text-sm"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-yellow-600 focus:border-transparent outline-none transition-all resize-none text-sm"
                      placeholder="Parlez-nous de votre projet..."
                    ></textarea>
                    <div className="text-sm text-slate-500 mt-1">
                      {formData.message.length}/500 caractères
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Envoi en cours...' : 'Démarrer mon projet'}
                    <i className="ri-send-plane-line ml-2"></i>
                  </Button>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <i className="ri-checkbox-circle-line text-xl"></i>
                        <span>Message envoyé avec succès ! Nous vous répondrons dans les 24h.</span>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                      <div className="flex flex-col space-y-2">
                        <div className="flex items-center space-x-2">
                          <i className="ri-error-warning-line text-xl"></i>
                          <span className="font-semibold">Une erreur est survenue</span>
                        </div>
                        <p className="text-sm">
                          Si le problème persiste, contactez-nous directement par email ou téléphone.
                        </p>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-8">
                  Informations de contact
                </h2>

                <div className="space-y-6 mb-12">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-yellow-700"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a href="mailto:contact@21daywebsite.com" className="text-slate-600 hover:text-yellow-700 transition-colors cursor-pointer">
                        contact@21daywebsite.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-yellow-700"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Téléphone</h3>
                      <a href="tel:+33123456789" className="text-slate-600 hover:text-yellow-700 transition-colors cursor-pointer">
                        +33 1 23 45 67 89
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-2xl text-yellow-700"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Horaires</h3>
                      <p className="text-slate-600">Lundi - Vendredi : 9h - 18h</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-8 rounded-2xl">
                  <h3 className="text-2xl font-bold mb-4">Réponse rapide garantie</h3>
                  <p className="text-slate-300 mb-6 leading-relaxed">
                    Nous répondons à toutes les demandes dans les 24 heures ouvrées. Vous recevrez une proposition personnalisée adaptée à vos besoins.
                  </p>
                  <div className="flex items-center space-x-3 text-yellow-500">
                    <i className="ri-time-line text-2xl"></i>
                    <span className="font-semibold">Réponse sous 24h</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
